import { useState } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Stack, Typography } from '@mui/material';

import { useMutation } from '@redwoodjs/web';

import { EmailRecipientsInput } from 'src/components/EmailRecipientsInput';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { RESEND_SELF_BILLING_INVOICE } from 'src/graphql/mutations/selfBillingInvoice.mutation';

import CoverMessageInput from './CoverMessageInput';

interface SendSelfBillingInvoiceModalProps {
    handleCloseSendModal: () => void;
    invoiceId: number;
    partnerBillingContactEmail: string;
}

const SendSelfBillingInvoiceModal = (
    sendSelfBillingInvoiceModalProps: SendSelfBillingInvoiceModalProps
) => {
    const [sendRecipients, setSendRecipients] = useState<string[]>([
        sendSelfBillingInvoiceModalProps.partnerBillingContactEmail,
    ]);
    const [coverMessage, setCoverMessage] = useState<string>('');

    const [resendSelfBillingInvoice, { loading }] = useMutation(RESEND_SELF_BILLING_INVOICE, {
        onCompleted: (data) => {
            if (data) {
                showSuccessToast(`Self-Billing Invoice resent`);
                sendSelfBillingInvoiceModalProps.handleCloseSendModal();
            }
        },
        onError: (err: Error) => {
            showErrorToast(err.message);
        },
    });

    const handleResendInvoice = async () => {
        await resendSelfBillingInvoice({
            variables: {
                input: {
                    id: sendSelfBillingInvoiceModalProps?.invoiceId,
                    billingEmail: sendRecipients.join(','),
                    coverMessage,
                },
            },
        });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
            }}
        >
            <Typography
                sx={{
                    fontSize: '16px',
                    color: '#667085',
                    marginBottom: '16px',
                }}
            >
                Are you sure you want to resend this invoice?
            </Typography>
            <Box sx={{ gap: '16px', display: 'flex', flexDirection: 'column' }}>
                <EmailRecipientsInput
                    sendRecipients={sendRecipients}
                    setSendRecipients={setSendRecipients}
                />
                <CoverMessageInput minRows={3} setCoverMessage={setCoverMessage} />
            </Box>
            <Stack direction="row" justifyContent={'center'} spacing={2} sx={{ mt: 3 }}>
                <Button
                    variant="outlined-black"
                    onClick={sendSelfBillingInvoiceModalProps.handleCloseSendModal}
                    fullWidth
                    sx={{ width: '170px', height: '44px' }}
                >
                    Close
                </Button>

                <LoadingButton
                    variant="main"
                    onClick={handleResendInvoice}
                    fullWidth
                    loading={loading}
                    disabled={sendRecipients.length < 1}
                    sx={{ width: '170px', height: '44px' }}
                >
                    Send
                </LoadingButton>
            </Stack>
        </Box>
    );
};

export default SendSelfBillingInvoiceModal;
